// src/pages/AboutPage.tsx
// "Om oss" — about page that tells the Nexilink story.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext.tsx';
import {
    LayoutDashboard,
    ArrowLeft,
    LogIn,
    X,
    Menu,
    Flag,
    Heart,
    Brain,
    Users,
} from 'lucide-react';
import { Footer } from '@/components/landing/Footer.tsx';
import { ContactCTA } from '@/components/landing/ContactCTA.tsx';
import { LoginForm } from '@/components/signin/LoginForm.tsx';
import AppHeader from '@/components/layout/AppHeader';
import { cn } from '@/lib/utils.ts';
import CONFIG from '@/lib/config';

const AboutPage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [loginOpen, setLoginOpen] = useState(false);

    // Lock body scroll when login panel is open
    useEffect(() => {
        document.body.style.overflow = loginOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [loginOpen]);

    // Close on Escape
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setLoginOpen(false);
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, []);

    const handleLoginSuccess = (success: boolean) => {
        if (success) {
            setLoginOpen(false);
            navigate('/dashboard');
        }
    };

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 10);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="flex overflow-hidden flex-col bg-neutral-100">
            {/* ==================== STICKY HEADER ==================== */}
            <AppHeader
                position="fixed"
                scrolled={scrolled}
                rightContent={
                    <>
                        {/* Desktop Nav */}
                        <nav className="hidden md:flex items-center gap-1 mr-2">
                            <button
                                onClick={() => navigate('/')}
                                className={cn(
                                    'px-4 py-2 text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2',
                                    scrolled ? 'text-[#212121] hover:bg-gray-50' : 'text-[#f5f5f5] hover:text-[#f5f5f5] hover:bg-[#f5f5f5]/10',
                                )}
                            >
                                <ArrowLeft className="w-4 h-4" />
                                Tillbaka
                            </button>
                        </nav>

                        {/* Action buttons */}
                        <div className="hidden md:flex items-center gap-3">
                            {CONFIG.PUBLIC_APP_ENABLED && user && (
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className={cn(
                                        'h-[54px] inline-flex items-center justify-center gap-2 px-5 text-sm font-medium rounded-lg transition-colors',
                                        scrolled ? 'text-[#1A4D4F] border-2 border-[#1A4D4F] hover:bg-[#1A4D4F]/5' : 'text-[#f5f5f5] border-2 border-[#f5f5f5]/30 hover:bg-[#f5f5f5]/10',
                                    )}
                                >
                                    <LayoutDashboard className="w-4 h-4" />
                                    Dashboard
                                </button>
                            )}
                            {CONFIG.PUBLIC_APP_ENABLED && !user && (
                                <button
                                    onClick={() => { setLoginOpen(true); setMobileMenuOpen(false); }}
                                    className={cn(
                                        'w-[124px] h-[54px] inline-flex items-center justify-center text-sm font-medium rounded-lg transition-colors',
                                        scrolled ? 'text-[#1A4D4F] border-2 border-[#1A4D4F] hover:bg-[#1A4D4F]/5' : 'text-[#f5f5f5] border-2 border-[#f5f5f5]/30 hover:bg-[#f5f5f5]/10',
                                    )}
                                >
                                    Logga in
                                </button>
                            )}
                        </div>

                        {/* Mobile menu button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className={cn(
                                'md:hidden p-2 rounded-lg',
                                scrolled ? 'text-[#212121] hover:bg-gray-50' : 'text-[#f5f5f5] hover:bg-[#f5f5f5]/10',
                            )}
                        >
                            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                        </button>
                    </>
                }
            />

            {/* Mobile Menu */}
            {mobileMenuOpen && (
                <div className="fixed top-16 sm:top-[72px] left-0 right-0 z-40 bg-white md:hidden border-b border-gray-100 py-3 space-y-1 px-4 sm:px-6 animate-in fade-in slide-in-from-top-2 duration-200">
                    <button
                        onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                        className="block w-full text-left px-4 py-2.5 text-sm font-medium text-[#212121] hover:bg-gray-50 rounded-lg"
                    >
                        Tillbaka till startsidan
                    </button>
                    {CONFIG.PUBLIC_APP_ENABLED && (
                        <div className="border-t border-gray-100 pt-3 mt-2 space-y-2 px-4">
                            {user ? (
                                <button
                                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-[#212121] border border-gray-200 rounded-lg hover:bg-gray-50"
                                >
                                    <LayoutDashboard className="w-4 h-4" />
                                    Dashboard
                                </button>
                            ) : (
                                <button
                                    onClick={() => { navigate('/?login=1'); setMobileMenuOpen(false); }}
                                    className="w-full px-4 py-2.5 text-sm font-medium text-[#212121] border border-gray-200 rounded-lg hover:bg-gray-50"
                                >
                                    Logga in
                                </button>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* HERO */}
            <section
                className="relative flex flex-col overflow-hidden justify-center items-start px-6 sm:px-12 md:px-20 pt-[72px] min-h-screen w-full text-[#f5f5f5] bg-cover bg-no-repeat bg-center"
                style={{
                    backgroundImage:
                        'url(https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80)',
                }}
            >
                <div className="absolute inset-0 bg-[#1A4D4F] opacity-70"></div>
                <div className="relative z-10 flex flex-col max-w-full w-full md:w-[796px]">
                    <div className="text-base sm:text-lg font-medium mb-4 text-teal-100">Om oss</div>
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium leading-tight md:leading-[72px]">
                        Vi finns för de som bär upp välfärden
                    </h1>
                    <p className="mt-6 text-lg sm:text-xl md:text-2xl leading-relaxed md:leading-9">
                        Nexilink grundades för att nya medarbetare i välfärden ska få en trygg start.
                        Vi digitaliserar introduktionen och låter medarbetare träna på verkliga situationer
                        innan de möter dem på riktigt.
                    </p>
                </div>
            </section>

            {/* OUR STORY */}
            <section className="flex flex-col justify-center px-6 sm:px-12 md:px-24 py-16 md:py-24 min-h-screen w-full bg-white">
                <div className="max-w-4xl mx-auto w-full">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#212121] leading-tight md:leading-[56px] mb-8">
                        Vår historia
                    </h2>
                    <div className="space-y-6 text-base sm:text-lg md:text-xl text-[#212121] leading-relaxed">
                        <p>
                            Varje år börjar tusentals nya medarbetare i välfärden utan en strukturerad
                            introduktion. Många slutar inom två år.
                        </p>
                        <p>
                            Abdi Ahmed Mohamud såg det på nära håll som socionom. Stressen, osäkerheten
                            och de misstag som hade kunnat undvikas med rätt förberedelse. Det är därför
                            Nexilink finns.
                        </p>
                        <p>
                            Vi bygger inte för att teknik är spännande. Onboarding i välfärden är idag
                            ofta ostrukturerad, tidskrävande och personberoende. Det är inte hållbart.
                            Vi bygger ett bättre sätt.
                        </p>
                    </div>
                </div>
            </section>

            {/* MISSION & VALUES */}
            <section className="flex flex-col justify-center px-6 sm:px-12 md:px-24 py-16 md:py-24 min-h-screen w-full bg-neutral-100">
                <div className="max-w-5xl mx-auto w-full">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#212121] leading-tight md:leading-[56px] mb-12 text-center">
                        Vårt uppdrag och våra värderingar
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Mission */}
                        <div className="flex flex-col bg-white p-8 rounded-2xl border border-neutral-200">
                            <div className="mb-4">
                                <Flag className="w-8 h-8 text-[#212121] stroke-[1.5]" />
                            </div>
                            <h3 className="text-2xl font-bold text-[#212121] mb-3">Vårt uppdrag</h3>
                            <p className="text-[#212121] leading-relaxed">
                                Att ge varje ny medarbetare i välfärden en rättvis chans att lyckas genom
                                en strukturerad, anpassad och digital introduktion som faktiskt fungerar.
                            </p>
                        </div>

                        {/* Empathy */}
                        <div className="flex flex-col bg-white p-8 rounded-2xl border border-neutral-200">
                            <div className="mb-4">
                                <Heart className="w-8 h-8 text-[#212121] stroke-[1.5]" />
                            </div>
                            <h3 className="text-2xl font-bold text-[#212121] mb-3">Empati först</h3>
                            <p className="text-[#212121] leading-relaxed">
                                Vi bygger för människor, inte processer. Allt vi gör utgår från medarbetarens
                                vardag och från chefens verklighet varje dag.
                            </p>
                        </div>

                        {/* Innovation */}
                        <div className="flex flex-col bg-white p-8 rounded-2xl border border-neutral-200">
                            <div className="mb-4">
                                <Brain className="w-8 h-8 text-[#212121] stroke-[1.5]" />
                            </div>
                            <h3 className="text-2xl font-bold text-[#212121] mb-3">AI med ansvar</h3>
                            <p className="text-[#212121] leading-relaxed">
                                Vi använder AI där det skapar verkligt värde — för att göra träning mer
                                realistisk, introduktion mer tillgänglig och chefens vardag lite lättare.
                                Aldrig som en gimmick.
                            </p>
                        </div>

                        {/* Community */}
                        <div className="flex flex-col bg-white p-8 rounded-2xl border border-neutral-200">
                            <div className="mb-4">
                                <Users className="w-8 h-8 text-[#212121] stroke-[1.5]" />
                            </div>
                            <h3 className="text-2xl font-bold text-[#212121] mb-3">Tillsammans med välfärden</h3>
                            <p className="text-[#212121] leading-relaxed">
                                Vi bygger inte i ett vakuum. Plattformen utvecklas i nära samarbete med
                                kommuner och chefer som vet vad som faktiskt behövs på golvet.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTACT CTA */}
            <section className="flex flex-col px-6 sm:px-12 md:px-24 py-16 md:py-24 w-full bg-[#d2e1d9]">
                <ContactCTA />
            </section>

            {/* FOOTER */}
            <Footer />

            {/* ==================== LOGIN SLIDE-OVER ==================== */}
            {loginOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm transition-opacity"
                    onClick={() => setLoginOpen(false)}
                />
            )}
            <div
                className={cn(
                    'fixed top-0 right-0 z-50 h-full w-full sm:w-[560px] lg:w-[640px] bg-white shadow-2xl transition-transform duration-300 ease-out flex flex-col',
                    loginOpen ? 'translate-x-0' : 'translate-x-full'
                )}
            >
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 flex-shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-teal-50 rounded-lg flex items-center justify-center">
                            <LogIn className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                            <h2 className="font-bold text-[#212121]">Logga in</h2>
                            <p className="text-xs text-[#212121]/60">Välkommen tillbaka</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setLoginOpen(false)}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    <LoginForm
                        onLoginCallback={handleLoginSuccess}
                        mode="embedded"
                    />
                </div>
            </div>
        </div>
    );
};

export default AboutPage;
